import type { GameEvent, SpawnCube } from "../net/protocol";
import { EvType } from "../net/protocol";
import { sampleChunkCubeColor, type ChunkRegistry } from "./chunks";

/** Клиентское представление падающей rigid-body группы.
 *  Сервер шлёт Spawn (один раз с составом) и Update'ы 20Hz (pos/vel/angle).
 *  Клиент гладко интерполирует: render использует
 *      pos_t = lastPos + lastVel * dt
 *      ang_t = lastAngle + lastAngVel * dt
 *  где dt = now - lastUpdateTime. Гравитацию не симулируем — это работа сервера. */
export interface FallingGroupClient {
  id: number;
  chunkId: number;
  cubeSize: number;
  pivot: [number, number, number];
  axis: [number, number, number];

  cubes: Array<{
    cubeIdx: number;
    rx: number; ry: number; rz: number;
    color: [number, number, number];
  }>;

  pos: [number, number, number];
  vel: [number, number, number];
  angle: number;
  angVel: number;
  lastUpdateTime: number; // performance.now() / 1000
}

export class FallingGroupRegistry {
  private groups = new Map<number, FallingGroupClient>();
  private _allCache: FallingGroupClient[] = [];
  private _allDirty = true;

  /** Spawn новой группы: сэмплируем цвета каждого куба из чанка ОДИН раз. */
  spawn(
    ev: Extract<GameEvent, { type: typeof EvType.FallingGroupSpawn }>,
    chunks: ChunkRegistry,
    now: number,
  ): void {
    const ch = chunks.get(ev.chunkId);
    const cubes = ev.cubes.map((c: SpawnCube) => {
      // Мировая позиция куба в исходной структуре = pivot + rel
      const wx = ev.pivot[0] + c.rx;
      const wy = ev.pivot[1] + c.ry;
      const wz = ev.pivot[2] + c.rz;
      const color: [number, number, number] = ch
        ? sampleChunkCubeColor(ch, wx, wy, wz, c.cubeIdx)
        : [0.6, 0.6, 0.6];
      return { cubeIdx: c.cubeIdx, rx: c.rx, ry: c.ry, rz: c.rz, color };
    });
    this.groups.set(ev.groupId, {
      id: ev.groupId,
      chunkId: ev.chunkId,
      cubeSize: ev.cubeSize,
      pivot: [ev.pivot[0], ev.pivot[1], ev.pivot[2]],
      axis: [ev.axis[0], ev.axis[1], ev.axis[2]],
      cubes,
      pos: [0, 0, 0],
      vel: [ev.vel[0], ev.vel[1], ev.vel[2]],
      angle: 0,
      angVel: ev.angVel,
      lastUpdateTime: now,
    });
    this._allDirty = true;
  }

  update(
    ev: Extract<GameEvent, { type: typeof EvType.FallingGroupUpdate }>,
    now: number,
  ): void {
    const g = this.groups.get(ev.groupId);
    if (!g) return;
    g.pos[0] = ev.pos[0]; g.pos[1] = ev.pos[1]; g.pos[2] = ev.pos[2];
    g.vel[0] = ev.vel[0]; g.vel[1] = ev.vel[1]; g.vel[2] = ev.vel[2];
    g.angle = ev.angle;
    g.angVel = ev.angVel;
    g.lastUpdateTime = now;
    this._allDirty = true;
  }

  /** При shatter: вызываем debrisSpawn для каждого куба в его текущей мировой
   *  позиции — клиент сам спавнит частицы. После — удаляем группу. */
  shatter(
    id: number,
    now: number,
    debrisSpawn: (x: number, y: number, z: number, vx: number, vy: number, vz: number,
                  color: [number, number, number], size: number) => void,
  ): void {
    const g = this.groups.get(id);
    if (!g) return;
    const dt = Math.max(0, now - g.lastUpdateTime);
    const px = g.pos[0] + g.vel[0] * dt;
    const py = g.pos[1] + g.vel[1] * dt;
    const pz = g.pos[2] + g.vel[2] * dt;
    const ang = g.angle + g.angVel * dt;
    for (const c of g.cubes) {
      const r = rotateAxisAngle(c.rx, c.ry, c.rz, g.axis[0], g.axis[1], g.axis[2], ang);
      const wx = g.pivot[0] + px + r[0];
      const wy = g.pivot[1] + py + r[1];
      const wz = g.pivot[2] + pz + r[2];
      // Импульс при shatter: текущая скорость группы + случайный разлёт
      const vx = g.vel[0] + (Math.random() - 0.5) * 2.0;
      const vy = g.vel[1] + (Math.random() - 0.5) * 2.0;
      const vz = g.vel[2] + Math.random() * 1.5;
      debrisSpawn(wx, wy, wz, vx, vy, vz, c.color, g.cubeSize);
    }
    this.groups.delete(id);
    this._allDirty = true;
  }

  all(): FallingGroupClient[] {
    if (this._allDirty) {
      const out = this._allCache;
      out.length = 0;
      for (const g of this.groups.values()) out.push(g);
      this._allDirty = false;
    }
    return this._allCache;
  }

  clear(): void {
    this.groups.clear();
    this._allCache.length = 0;
    this._allDirty = false;
  }

  getCubeCount(id: number): number {
    const g = this.groups.get(id);
    return g ? g.cubes.length : 0;
  }

  get(id: number): FallingGroupClient | undefined {
    return this.groups.get(id);
  }
}

/** Rodrigues' rotation formula: повернуть вектор v на angle вокруг axis k.
 *  v_rot = v*cos + (k×v)*sin + k*(k·v)*(1-cos) */
export function rotateAxisAngle(
  vx: number, vy: number, vz: number,
  kx: number, ky: number, kz: number,
  angle: number,
): [number, number, number] {
  if (angle === 0) return [vx, vy, vz];
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const omc = 1 - c;
  const cxx = ky * vz - kz * vy;
  const cyy = kz * vx - kx * vz;
  const czz = kx * vy - ky * vx;
  const kdv = kx * vx + ky * vy + kz * vz;
  return [
    vx * c + cxx * s + kx * kdv * omc,
    vy * c + cyy * s + ky * kdv * omc,
    vz * c + czz * s + kz * kdv * omc,
  ];
}
